import createGeoJsonDataSource from '~app/core/createGeoJsonDataSource';
import createProjectedText from '~app/core/createProjectedText';
import Location from '~app/core/location';

export const LOADED_GRID_VECTOR = 'LOADED_GRID_VECTOR';
export const LOADED_BORDER_VECTOR = 'LOADED_BORDER_VECTOR';

export const SHOW_GRID = 'SHOW_GRID';
export const SHOW_BORDERS = 'SHOW_BORDERS';

// Loading

// Define the list of vectors to load and the rendering options for each set.
const vectorConfig = {
  // We start with some default sizes for the major and minor labels.
  // Then we adjust the widths to better fit labels that deviate too much from
  // the default to prevent stretching.
  // For the tropics, we have to increase the height to allow for the 'p' characters
  // which drop below the baseline and increase the overall height of the text.
  "majorCircles": {
    "layerDefs": [
      {
        "layerUrl": require('~app/resources/vector/grid/arctic_circle.geojson'),
        "labels": generateMajorLabelDefs(
          require('~app/resources/images/labels/Arctic_Circle.png'), 66.562861111, {width:80}, {vertical:"top"}),
      },
      {
        "layerUrl": require('~app/resources/vector/grid/antarctic_circle.geojson'),
        "labels": generateMajorLabelDefs(
          require('~app/resources/images/labels/Antarctic_Circle.png'), -66.562861111, {width:90}, {vertical:"bottom"}),
      },
      {
        "layerUrl": require('~app/resources/vector/grid/tropic_of_cancer.geojson'),
        "labels": generateMajorLabelDefs(
          require('~app/resources/images/labels/Tropic_Of_Cancer.png'), 23.43713, {width: 110, height:17.5}, {vertical:"top"}),
      },
      {
        "layerUrl": require('~app/resources/vector/grid/tropic_of_capricorn.geojson'),
        "labels": generateMajorLabelDefs(
          require('~app/resources/images/labels/Tropic_Of_Capricorn.png'), -23.43713, {width: 120, height:17.5}, {vertical:"bottom"}),
      },
    ],
    "options": {
      stroke: [255.0, 240.0, 77.0],
      strokeWidth: 2.0,
    },
    "type": "grid",
  },
  "minorCircles": {
    "layerDefs": [
      {
        "layerUrl": require('~app/resources/vector/grid/graticules_15.geojson'),
        "labels": [
          ...generateMinorLabelDefs(
            require('~app/resources/images/labels/60N.png'), 60, {}, {vertical:"top"}),
          ...generateMinorLabelDefs(
            require('~app/resources/images/labels/30N.png'), 30, {}, {vertical:"top"}),
          ...generateMinorLabelDefs(
            require('~app/resources/images/labels/Equator.png'), 0, {width:16}, {vertical: "bottom"}),
          ...generateMinorLabelDefs(
            require('~app/resources/images/labels/30S.png'), -30, {}, {vertical: "bottom"}),
          ...generateMinorLabelDefs(
            require('~app/resources/images/labels/60S.png'), -60, {}, {vertical: "bottom"}),
        ],
      },
    ],
    "options": {
      stroke: [104.0, 242.0, 238.0],
      strokeWidth: 1.0,
    },
    "type": "grid",
  },
  "borders": {
    "layerDefs": [
      {
        "layerUrl": require('~app/resources/vector/borders/country_50m.geojson'),
        "labels": [],
      },
    ],
    "options": {},
    "type": "border",
  },
};

function generateMajorLabelDefs(
  image,
  lat,
  {width = 100.0, height = 12.0} = {},
  {horizontal = "center", vertical = "center"} = {},
  num = 4) {
  return generateLatitudeLabelDefs(
    image,
    lat,
    {width, height},
    {horizontal, vertical},
    num);
}

function generateMinorLabelDefs(
  image,
  lat,
  {width = 32.0, height = 12.0} = {},
  {horizontal = "center", vertical = "center"} = {},
  num = 4) {
  return generateLatitudeLabelDefs(
    image,
    lat,
    {width, height},
    {horizontal, vertical},
    num);
}

function generateLatitudeLabelDefs(image, lat, size, align, num) {
  const labels = [];
  for (let i = 0; i < num; ++i) {
    const lon = i * (360.0 / num);
    labels.push({
      location: new Location([lon * Math.PI / 180.0, lat * Math.PI / 180.0]),
      image,
      size,
      align,
    });
  }
  return labels;
}

export function loadAllVectors(updateProgress) {
  return async (dispatch) => {
    const load = async (vectorSet, layerDef, loaderFn) => {
      const result = await dispatch(loaderFn(
        layerDef.layerUrl,
        vectorSet.options,
        layerDef.labels,
      ));
      updateProgress();
      return result;
    };

    // Aggregate a set of promises to load all vectors.
    const allPromises = Object.keys(vectorConfig)
      // Convert from a dictionary with named keys to an array of vector configs.
      .map(key => vectorConfig[key])
      // Extract a set of promises from each vector config and add them to a
      // flattened array of promises.
      .reduce((workPromises, vectorSet) => {
        // Choose an appropriate loading function based on the vector type.
        let loaderFunction = null;
        if (vectorSet.type === "grid") {
          loaderFunction = loadGridVector;
        }
        else if (vectorSet.type === "border") {
          loaderFunction = loadBorderVector;
        }
        else {
          throw new Error(
            `Failed to parse vector type ${vectorSet.type}. \
            Supported options are 'grid' and 'border'.`,
          );
        }

        // For each vector name, invoke the loader to create a promise
        // and concatenate them all into the flattened result array.
        workPromises.push(
          ...vectorSet.layerDefs.map(layerDef =>
              load(vectorSet, layerDef, loaderFunction)));

        return workPromises;
      }, []);

    await Promise.all(allPromises);
  };
}

export function countVectors() {
  return Object.keys(vectorConfig)
    .map(key => vectorConfig[key].layerDefs.length)
    .reduce((sum, val) => sum + val, 0);
}

function loadGridVector(name, options, labels) {
  return async (dispatch) => {
    const source = await createGeoJsonDataSource(name, options);
    dispatch({
      type: LOADED_GRID_VECTOR,
      geoJsonDataSource: source,
      globeLabels: labels.map(labelDef => createProjectedTextFromLabelDef(
        labelDef, true)),
      mapLabels: labels.map(labelDef => createProjectedTextFromLabelDef(
        labelDef, false)),
    });
    return source;
  };
}

function loadBorderVector(name, options, labels) {
  return async (dispatch) => {
    const source = await createGeoJsonDataSource(name, options);
    dispatch({
      type: LOADED_BORDER_VECTOR,
      geoJsonDataSource: source,
      globeLabels: labels.map(labelDef => createProjectedTextFromLabelDef(
        labelDef, true)),
      mapLabels: labels.map(labelDef => createProjectedTextFromLabelDef(
        labelDef, false)),
    });
    return source;
  };
}

function createProjectedTextFromLabelDef(labelDef, applyGlobeProjection) {
  const {location, image, size, align} = labelDef;
  return createProjectedText(location, image, size, align, applyGlobeProjection);
}

// Settings menu

export function showGrid(visible = true) {
  return {
    type: SHOW_GRID,
    visible,
  };
}

export function showBorders(visible = true) {
  return {
    type: SHOW_BORDERS,
    visible,
  };
}



// WEBPACK FOOTER //
// ./src/actions/vector.js